/**
 * How a SIREN and a SIRET are written for a human to read.
 *
 * This file is consumed by `@shaarp/ui`. `@shaarp/api` cannot import a runtime
 * value from this package (its barrel re-exports a directory, which Node
 * refuses to resolve from the API's CommonJS build), so it keeps a mirrored
 * copy in `api/src/common/utils/formats.ts`, held to this one by
 * `api/src/common/utils/formats.spec.ts`.
 *
 * Keep both files in sync. A mismatch spells the same number two ways on the
 * same screen, which is exactly what this file was extracted to end: the chat's
 * identifier card grouped a SIRET as the registry prints it, while the number
 * stored on the company had been grouped three digits at a time.
 */

/** What people type or paste between the digits: spaces, dots, dashes. */
const SEPARATORS = /[\s\u00A0\u202F.-]/g;

/** The digits alone, whatever the number was written with. */
const digitsOf = (value: string): string => value.replace(SEPARATORS, "");

/**
 * A SIREN in threes, the way the registry prints it: `888 147 337`. Named as
 * the API names it, so the mirror is unmistakable — and not `formatSiren`,
 * which over there strips the number down to its digits.
 *
 * Returns `null` for a nullish input, and leaves anything that is not nine
 * digits as it came: a number we cannot recognise is shown as it was given
 * rather than regrouped into a shape it does not have.
 */
export const formatSirenFormatted = (
  siren: string | null | undefined,
): string | null => {
  if (siren == null) {
    return null;
  }

  const digits = digitsOf(siren);
  if (!/^\d{9}$/.test(digits)) {
    return siren.trim() || null;
  }

  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

/**
 * A SIRET as the registry prints it: its SIREN in threes, then the five
 * digits of the establishment in one block — `888 147 337 00016`. The NIC is
 * one number and is never cut in half.
 *
 * Returns `null` for a nullish input, and leaves anything that is not fourteen
 * digits as it came.
 */
export const formatSiretFormatted = (
  siret: string | null | undefined,
): string | null => {
  if (siret == null) {
    return null;
  }

  const digits = digitsOf(siret);
  if (!/^\d{14}$/.test(digits)) {
    return siret.trim() || null;
  }

  return `${formatSirenFormatted(digits.slice(0, 9))} ${digits.slice(9)}`;
};
